import { authenticatedHttpClient } from './httpClient';
import {
  EnableMultiWarehouseProgress,
  EnableMultiWarehouseResult,
  Warehouse,
  WarehouseCreatePayload,
  WAREHOUSE_LIST_PAGE_SIZE,
  WarehouseQueryResult,
  WarehouseUpdatePayload,
} from '../models/warehouse';
import { StockBalance, StockBalanceQueryResult } from '../models/stockBalance';

export const listWarehouses = async (
  organizationId: string,
  options?: {
    q?: string;
    activeOnly?: boolean;
    limit?: number;
    cursor?: string;
  }
): Promise<WarehouseQueryResult> => {
  const params = new URLSearchParams();
  if (options?.q) params.append('q', options.q);
  if (options?.activeOnly) params.append('active_only', 'true');
  params.append('limit', String(options?.limit ?? WAREHOUSE_LIST_PAGE_SIZE));
  if (options?.cursor) params.append('cursor', options.cursor);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return authenticatedHttpClient.get<WarehouseQueryResult>(
    `/warehouses/${organizationId}${suffix}`
  );
};

export const listAllActiveWarehouses = async (
  organizationId: string
): Promise<Warehouse[]> => {
  const items: Warehouse[] = [];
  let cursor: string | undefined;
  let hasMore = true;

  while (hasMore) {
    const result = await listWarehouses(organizationId, {
      activeOnly: true,
      limit: 50,
      cursor,
    });
    items.push(...(result.items || []));
    hasMore = Boolean(result.hasMore && result.nextCursor);
    cursor = result.nextCursor ?? undefined;
    if (!cursor) {
      hasMore = false;
    }
  }

  return items;
};

export const createWarehouse = async (
  organizationId: string,
  payload: WarehouseCreatePayload
): Promise<Warehouse> => {
  return authenticatedHttpClient.post<Warehouse>(
    `/warehouses/${organizationId}`,
    payload
  );
};

export const updateWarehouse = async (
  organizationId: string,
  warehouseId: string,
  payload: WarehouseUpdatePayload
): Promise<Warehouse> => {
  return authenticatedHttpClient.put<Warehouse>(
    `/warehouses/${organizationId}/${warehouseId}`,
    payload
  );
};

export const deleteWarehouse = async (
  organizationId: string,
  warehouseId: string
): Promise<void> => {
  await authenticatedHttpClient.delete(
    `/warehouses/${organizationId}/${warehouseId}`
  );
};

/** Opt in to multi-warehouse: default warehouse + stock balance backfill (REST; small catalogs). */
export const enableMultiWarehouse = async (
  organizationId: string
): Promise<EnableMultiWarehouseResult> => {
  return authenticatedHttpClient.post<EnableMultiWarehouseResult>(
    `/warehouses/${organizationId}/enable-multi`,
    {},
    { timeout: 300000 }
  );
};

/**
 * Enable multi-warehouse via Socket.IO: paginated backfill, progress, no request timeout.
 * All-or-nothing: on failure or disconnect, partial balances are rolled back.
 */
export async function enableMultiWarehouseViaWebSocket(
  organizationId: string,
  onProgress: (progress: EnableMultiWarehouseProgress) => void
): Promise<EnableMultiWarehouseResult> {
  const baseURL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
  const { io } = await import('socket.io-client');

  return new Promise((resolve, reject) => {
    const socket = io(baseURL, {
      transports: ['polling'],
      timeout: 60000,
      forceNew: true,
    });
    let resolved = false;
    const cleanup = () => {
      socket.off('connected');
      socket.off('progress');
      socket.off('success');
      socket.off('error');
      socket.off('connect_error');
      socket.disconnect();
    };

    socket.on('connect_error', err => {
      if (!resolved) {
        resolved = true;
        cleanup();
        reject(err || new Error('Socket connection failed'));
      }
    });

    socket.on('connected', () => {
      socket.emit('enableMultiWarehouse', { organizationId });
    });

    socket.on(
      'progress',
      (data: {
        scanned?: number;
        total?: number;
        productsProcessed?: number;
        balancesCreated?: number;
        message?: string;
      }) => {
        if (typeof data.scanned === 'number' && typeof data.total === 'number') {
          onProgress({
            scanned: data.scanned,
            total: data.total,
            productsProcessed: data.productsProcessed ?? 0,
            balancesCreated: data.balancesCreated ?? 0,
            message: data.message,
          });
        }
      }
    );

    socket.on('success', (data: EnableMultiWarehouseResult) => {
      if (!resolved) {
        resolved = true;
        cleanup();
        resolve(data);
      }
    });

    socket.on('error', (payload: { message?: string }) => {
      if (!resolved) {
        resolved = true;
        cleanup();
        reject(new Error(payload?.message ?? 'Failed to enable multi-warehouse'));
      }
    });
  });
}

export const listStockBalancesForProduct = async (
  organizationId: string,
  productId: string
): Promise<StockBalance[]> => {
  const params = new URLSearchParams({ product_id: productId });
  const result = await authenticatedHttpClient.get<StockBalanceQueryResult>(
    `/warehouses/${organizationId}/stock-balances?${params.toString()}`
  );
  return result.items || [];
};

export interface ProductStockBalances {
  productId: string;
  balances: StockBalance[];
  totalSellable: number;
}

export interface BulkStockBalancesResponse {
  products: ProductStockBalances[];
}

export const listStockBalancesForProducts = async (
  organizationId: string,
  productIds: string[]
): Promise<ProductStockBalances[]> => {
  const uniqueIds = Array.from(
    new Set(productIds.map(id => String(id || '').trim()).filter(Boolean))
  );
  if (uniqueIds.length === 0) return [];

  const result = await authenticatedHttpClient.post<BulkStockBalancesResponse>(
    `/warehouses/${organizationId}/stock-balances/bulk`,
    { productIds: uniqueIds }
  );
  return result.products || [];
};

export const listStockBalancesForWarehouse = async (
  organizationId: string,
  warehouseId: string
): Promise<StockBalance[]> => {
  const params = new URLSearchParams({ warehouse_id: warehouseId });
  const result = await authenticatedHttpClient.get<StockBalanceQueryResult>(
    `/warehouses/${organizationId}/stock-balances?${params.toString()}`
  );
  return result.items || [];
};

export interface StockMovementResult {
  productId: string;
  warehouseId?: string;
  oldAvailability: number;
  newAvailability: number;
}

export const adjustWarehouseStock = async (
  organizationId: string,
  payload: {
    productId: string;
    warehouseId: string;
    targetOnHand: number;
    reason?: string;
  }
): Promise<StockMovementResult> => {
  return authenticatedHttpClient.post<StockMovementResult>(
    `/warehouses/${organizationId}/stock-adjustments`,
    payload
  );
};

export const transferWarehouseStock = async (
  organizationId: string,
  payload: {
    productId: string;
    fromWarehouseId: string;
    toWarehouseId: string;
    quantity: number;
    reason?: string;
  }
): Promise<StockMovementResult> => {
  return authenticatedHttpClient.post<StockMovementResult>(
    `/warehouses/${organizationId}/stock-transfers`,
    payload
  );
};
